import React, { useCallback, useEffect, useState } from "react";
import { FeatherAlertTriangle } from "@subframe/core";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Switch } from "../../../../ui/components/Switch";
import {
  deactivateTeamMember,
  fetchEscalationContacts,
  fetchTeamMembers,
  reactivateTeamMember,
  type EscalationContact,
  type TeamMember,
} from "../../../../shared/data/api/teamApi";
import { initials, memberLabel } from "./teamAssignment";
import {
  clientLogins,
  formatLastSeen,
  receivesClientDigest,
  receivesNothing,
  receivesTeamDigest,
} from "./escalationsAccess";

/**
 * The two mailer-caveat callouts ("N people receive no email at all" and
 * "No escalation order") are HIDDEN for now (Prasanha, 2026-08-21): with ZN
 * maker/checker roles on staff the amber warning fires constantly and reads
 * as an error. The facts they state are still true — flip this to show them.
 */
const SHOW_MAILER_CAVEATS = false;

const PersonRow: React.FC<{
  member: TeamMember;
  aside?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ member, aside, action }) => (
  <li className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 border-b border-solid border-neutral-border px-4 py-2.5 last:border-b-0">
    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-neutral-100 text-caption-bold font-caption-bold text-subtext-color">
      {initials(member)}
    </span>
    <span className="flex min-w-0 grow shrink-0 basis-48 flex-col">
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-body font-body text-default-font break-words">
          {memberLabel(member)}
        </span>
        {!member.isActive && <Badge variant="neutral">Inactive</Badge>}
      </span>
      <span className="text-caption font-caption text-subtext-color break-words">
        {member.email}
      </span>
    </span>
    {aside}
    {action}
  </li>
);

const Section: React.FC<{
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="flex w-full flex-col gap-3">
    <div className="flex flex-col gap-1">
      <span className="text-heading-3 font-heading-3 text-default-font">{title}</span>
      {description !== undefined && (
        <span className="text-body font-body text-subtext-color">{description}</span>
      )}
    </div>
    {children}
  </section>
);

/**
 * Who hears about the work, and who can see the books.
 *
 * THREE PARTS, AND ONLY THE LAST IS EDITABLE.
 *
 * First, the wizard's ordered escalation contact list, pushed from master at
 * go-live into `escalation_contacts`. Master owns it wholesale (its go-live
 * step empties and re-fills the table), so it is shown read-only — and it is
 * DISPLAY-ONLY: `EmailNotificationService` still picks recipients with SQL
 * over `users.role` and never reads this table. Wiring the mailer to the
 * order is a recorded follow-up, not something this page pretends exists.
 *
 * Second, who those role rules resolve to today, pointing at the screen that
 * changes it, which is Team assignment.
 *
 * Third, the editable part: client logins are `client_view` users, with an
 * enable/disable switch per row (disable = the old remove flow with its
 * confirm; enable = POST /api/users/reactivate, no confirm — restoring access
 * is the safe direction). They still cannot be CREATED here: user ids are
 * minted by master admin.
 */
export const EscalationsAccessList: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [contacts, setContacts] = useState<EscalationContact[] | null>(null);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [enablingId, setEnablingId] = useState<number | null>(null);

  const load = useCallback(
    () => fetchTeamMembers().then((result) => setMembers(result.members)),
    [],
  );

  useEffect(() => {
    let live = true;
    load().catch((err: unknown) => {
      if (live) {
        setLoadError(err instanceof Error ? err.message : "Could not load the people.");
      }
    });
    // Loaded apart from the people on purpose: a failure here must not blank
    // the digest report below, and vice versa.
    fetchEscalationContacts()
      .then((rows) => {
        if (live) setContacts(rows);
      })
      .catch((err: unknown) => {
        if (live) {
          setContactsError(
            err instanceof Error ? err.message : "Could not load the escalation contacts.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [load]);

  const enableAccess = async (member: TeamMember) => {
    setEnablingId(member.id);
    setNotice(null);
    setLoadError(null);
    try {
      await reactivateTeamMember(member.id);
      setNotice(`${memberLabel(member)} can sign in again.`);
      await load();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not restore access.");
    } finally {
      setEnablingId(null);
    }
  };

  const confirmRemove = async () => {
    if (removing === null) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await deactivateTeamMember(removing.id);
      const name = memberLabel(removing);
      setRemoving(null);
      setNotice(`${name} can no longer sign in.`);
      await load();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : "Could not remove access.");
    } finally {
      setRemoveBusy(false);
    }
  };

  const all = members ?? [];
  const team = all.filter(receivesTeamDigest);
  const clients = all.filter(receivesClientDigest);
  const silent = all.filter(receivesNothing);
  const logins = clientLogins(all);

  return (
    <div className="flex w-full flex-col gap-8">
      {loadError !== null && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{loadError}</span>
        </div>
      )}

      {notice !== null && (
        <div className="flex w-full items-start rounded-lg border border-solid border-green-200 bg-green-50 p-3">
          <span className="text-body font-body text-green-800">{notice}</span>
        </div>
      )}

      {/* Master-owned and display-only: the go-live push refreshes the table
          wholesale, and the mailer does not read it — see escalationsAccess.ts. */}
      <Section
        title="Escalation contacts"
        description="Contact these people when something needs attention — in this order. The list is set in master admin. Emails do not follow this order yet."
      >
        {contactsError !== null && (
          <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
            <span className="text-body font-body text-red-600">{contactsError}</span>
          </div>
        )}
        {contacts === null && contactsError === null && (
          <span className="text-body font-body text-subtext-color">
            Reading the contacts...
          </span>
        )}
        {contacts !== null && contacts.length === 0 && (
          <div className="flex w-full items-start rounded-lg border border-solid border-neutral-border bg-neutral-50 p-3">
            <span className="text-body font-body text-subtext-color">
              No escalation contacts have been pushed from master yet.
            </span>
          </div>
        )}
        {contacts !== null && contacts.length > 0 && (
          <div className="flex w-full flex-col rounded-md border border-solid border-neutral-border">
            <ul className="flex w-full flex-col">
              {contacts.map((contact) => (
                <li
                  key={contact.id}
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 border-b border-solid border-neutral-border px-4 py-2.5 last:border-b-0"
                >
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-neutral-100 text-caption-bold font-caption-bold text-subtext-color">
                    {contact.position}
                  </span>
                  <span className="flex min-w-0 grow shrink-0 basis-48 flex-col">
                    <span className="text-body font-body text-default-font break-words">
                      {contact.name}
                    </span>
                    <span className="text-caption font-caption text-subtext-color break-words">
                      {contact.email}
                    </span>
                  </span>
                  {contact.phone !== null && (
                    <span className="shrink-0 text-body font-body text-subtext-color">
                      {contact.phone}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {members === null && loadError === null && (
        <span className="text-body font-body text-subtext-color">
          Reading the people...
        </span>
      )}

      {members !== null && (
        <>
          {/* Description ("Worked out from each person's role ... change their
              role on Team assignment") hidden on Prasanha's review 2026-08-21,
              same pass as the mailer caveats. Still true: recipients come from
              roles, and Team assignment is where that changes. */}
          <Section title="Who gets notified">
            <div className="flex w-full flex-col rounded-md border border-solid border-neutral-border">
              <div className="flex w-full items-center justify-between gap-2 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Team digests
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {team.length} {team.length === 1 ? "person" : "people"}
                </span>
              </div>
              <ul className="flex w-full flex-col">
                {team.map((member) => (
                  <PersonRow
                    key={member.id}
                    member={member}
                    aside={
                      <span className="shrink-0 text-body font-body text-subtext-color">
                        {member.role}
                      </span>
                    }
                  />
                ))}
              </ul>
            </div>

            <div className="flex w-full flex-col rounded-md border border-solid border-neutral-border">
              <div className="flex w-full items-center justify-between gap-2 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Client digests
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {clients.length} {clients.length === 1 ? "person" : "people"}
                </span>
              </div>
              {clients.length === 0 ? (
                <span className="px-4 py-3 text-body font-body text-subtext-color">
                  Nobody. The client receives no digests.
                </span>
              ) : (
                <ul className="flex w-full flex-col">
                  {clients.map((member) => (
                    <PersonRow key={member.id} member={member} />
                  ))}
                </ul>
              )}
            </div>

            {/* Silent by accident rather than by choice: the mailer matches on
                role names in SQL, so anyone holding a role it does not list is
                simply never emailed, with nothing anywhere to say so.
                HIDDEN for now (Prasanha, 2026-08-21) — with ZN maker/checker
                roles on staff the warning fires constantly and reads as an
                error. Flip SHOW_MAILER_CAVEATS to bring it and the
                "No escalation order" note back. */}
            {SHOW_MAILER_CAVEATS && silent.length > 0 && (
              <div className="flex w-full flex-col gap-1 rounded-lg border border-solid border-amber-200 bg-amber-50 p-3">
                <span className="text-body-bold font-body-bold text-amber-800">
                  {silent.length} {silent.length === 1 ? "person receives" : "people receive"}{" "}
                  no email at all
                </span>
                <span className="text-body font-body text-amber-800">
                  {silent.map((m) => `${memberLabel(m)} (${m.role ?? "no role"})`).join(", ")}
                  . Notifications are matched on role name, so a role the mailer
                  does not know about gets nothing.
                </span>
              </div>
            )}

            {SHOW_MAILER_CAVEATS && (
            <div className="flex w-full flex-col gap-1 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <span className="text-body-bold font-body-bold text-default-font">
                No escalation order
              </span>
              <span className="text-body font-body text-subtext-color">
                Everyone on a list is notified together. The escalation
                contacts above set an order, but emails do not follow it yet —
                there is no first contact, no waiting period and no second
                contact if nobody acts.
              </span>
            </div>
            )}
          </Section>

          <Section title="Client dashboard access">
            {logins.length === 0 ? (
              <div className="flex w-full items-start rounded-lg border border-solid border-neutral-border bg-neutral-50 p-3">
                <span className="text-body font-body text-subtext-color">
                  No client login exists for these books.
                </span>
              </div>
            ) : (
              <div className="flex w-full flex-col rounded-md border border-solid border-neutral-border">
                <ul className="flex w-full flex-col">
                  {logins.map((member) => {
                    const seen = formatLastSeen(member.lastLoginAt);
                    return (
                      <PersonRow
                        key={member.id}
                        member={member}
                        aside={
                          <span className="shrink-0 text-caption font-caption text-subtext-color">
                            {seen === null ? "Never signed in" : `Last seen ${seen}`}
                          </span>
                        }
                        action={
                          /* Disabling asks first (they can't sign in again);
                             enabling doesn't (restoring access is the safe
                             direction, and it round-trips through the same
                             confirm if it was wrong). */
                          <Switch
                            checked={member.isActive}
                            disabled={enablingId !== null}
                            onCheckedChange={(next: boolean) => {
                              if (next) {
                                void enableAccess(member);
                              } else {
                                setRemoving(member);
                              }
                            }}
                            aria-label={
                              member.isActive
                                ? `Disable access for ${memberLabel(member)}`
                                : `Enable access for ${memberLabel(member)}`
                            }
                          />
                        }
                      />
                    );
                  })}
                </ul>
              </div>
            )}

            {/* "Giving someone access" note removed on Prasanha's review
                2026-08-21. The facts still hold: client logins are created in
                master admin (user ids are minted there), and re-enabling a
                removed login is master-side too. */}
          </Section>
        </>
      )}

      <DialogLayout
        open={removing !== null}
        onOpenChange={(open: boolean) => {
          if (!removeBusy && !open) {
            setRemoving(null);
            setRemoveError(null);
          }
        }}
      >
        <div className="flex w-[480px] max-w-full flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-100">
              <FeatherAlertTriangle className="text-error-600" />
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Remove dashboard access
              </span>
              <span className="text-body font-body text-subtext-color">
                {removing === null ? "" : memberLabel(removing)}
              </span>
            </div>
          </div>

          {/* "Cannot sign in AGAIN", not "loses access now": tenant JWTs
              carry no expiry and validateJWT never re-checks is_active, so a
              session that is already open keeps working. Cutting live
              sessions off needs an is_active check in validateJWT — a
              Prasanha decision, since it would apply to every endpoint. */}
          <span className="text-body font-body text-subtext-color">
            They cannot sign in again to see these books, and they stop
            receiving the client digests. A session that is already open is
            not cut off. Nothing in the books changes.
          </span>

          {removeError !== null && (
            <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
              <span className="text-body font-body text-red-600">{removeError}</span>
            </div>
          )}

          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-secondary"
              onClick={() => {
                setRemoving(null);
                setRemoveError(null);
              }}
              disabled={removeBusy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive-primary"
              onClick={confirmRemove}
              disabled={removeBusy}
            >
              {removeBusy ? "Removing..." : "Remove access"}
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};
